import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Truck, Package, Warehouse, ClipboardList, 
  ArrowRight, ChevronRight, CheckCircle2, 
  Clock, ShieldCheck, Route
} from 'lucide-react';
import Logistics from '../../assets/Logistics.png'; // Assuming you have an image in assets

const CONTAINER = 'w-full max-w-7xl xl:max-w-[95%] 2xl:max-w-[85vw] mx-auto px-6 sm:px-8 lg:px-8';
const THEME_COLOR = '#0284c7'; // Light Blue theme for Logistics

/* ============================== MOCKUP COMPONENTS ============================== */

// const BrandMark = ({ variant = 'gradient', imgClass = 'h-4', textClass = 'text-[10px]' }) => (
//   <span className="inline-flex items-center gap-1 leading-none">
//     <img
//       src="/smartedzmain.png"
//       alt="SmartEdz"
//       className={`${imgClass} w-auto object-contain`}
//       onError={(e) => { e.currentTarget.style.display = 'none'; }}
//     />
//     <span className={`${textClass} font-bold tracking-tight leading-none`}>
//       {variant === 'white' ? (
//         <span className="text-white">SmartEdz</span>
//       ) : (
//         <>
//           <span className="bg-gradient-to-t from-[#18469A] via-[#1B83C5] to-[#21BEE1] bg-clip-text text-transparent">Smart</span>
//           <span className="bg-gradient-to-t from-[#E35D14] to-[#F4A51D] bg-clip-text text-transparent">Edz</span>
//         </>
//       )}
//     </span>
//   </span>
// );

/* ============================== PAGE DATA ============================== */

const FEATURES = [
  { title: 'Fleet Management', icon: Truck, desc: 'Track your entire vehicle fleet in real-time, monitor driver behavior, and schedule routine maintenance.' },
  { title: 'Route Optimization', icon: Route, desc: 'Automatically calculate the most efficient delivery routes to save fuel and reduce transit times.' },
  { title: 'Warehouse Integration', icon: Warehouse, desc: 'Sync inventory levels with dispatch operations to ensure accurate order fulfillment and loading.' },
  { title: 'Shipment Tracking', icon: Package, desc: 'Provide customers with real-time tracking links, milestone updates, and secure proof of delivery (POD).' },
  { title: 'Dispatch & Scheduling', icon: ClipboardList, desc: 'Assign jobs to drivers automatically based on proximity, vehicle capacity, and working hours.' },
  { title: 'Compliance & Safety', icon: ShieldCheck, desc: 'Maintain digital logs for hours of service (HOS), vehicle inspections, and regulatory compliance.' }
];

const LogisticsERP = () => {
  // Use location hook to get routing state
  const location = useLocation();
  const navSource = location.state?.from === 'products' ? 'products' : 'industries';

  return (
    <div className="overflow-x-hidden pt-0">
      <style>{`
        @keyframes seFadeUp { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
        .se-fade-up { animation: seFadeUp .7s ease-out both; }
        @keyframes seFloat { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-12px)} }
        .se-float { animation: seFloat 6s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) { .se-float, .se-fade-up { animation: none !important; } }
      `}</style>

      {/* ================= HERO SECTION ================= */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#f0f9ff] via-[#f8fafc] to-white">
        <div className="pointer-events-none absolute -top-24 -right-24 w-96 h-96 rounded-full bg-primary/10 blur-3xl" />
        <div className="pointer-events-none absolute top-40 -left-24 w-80 h-80 rounded-full bg-[#0284c7]/10 blur-3xl" />
        <div className={`${CONTAINER} relative py-5 lg:py-6`}>
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-8 items-center">
            
            <div className="max-w-2xl relative z-10 se-fade-up">
              
              {/* Conditionally Render Navigation Based on State */}
              <nav className="flex flex-wrap items-center gap-2 text-sm text-slate-500 mb-6">
                <Link to="/" className="hover:text-primary transition-colors">Home</Link>
                <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
                <Link to={`/${navSource}`} className="hover:text-primary transition-colors capitalize">{navSource}</Link>
                <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
                <span className="text-primary font-medium">Logistics & Transport </span>
              </nav>

              <span className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.18em] uppercase text-primary bg-white ring-1 ring-primary/25 px-4 py-1.5 rounded-full mb-6">
                <Truck className="w-4 h-4 text-[#0284c7]" /> SmartEdz Supply Chain
              </span>
              <h1 className="text-4xl sm:text-5xl xl:text-[3.7rem] font-extrabold leading-[1.08] tracking-tight mb-6" style={{ color: '#13294b' }}>
                Complete Logistics & Transport Management
              </h1>
              <p className="text-lg text-slate-500 max-w-xl leading-relaxed mb-8">
                Streamline your supply chain. Manage fleets, optimize routes, and track shipments in real-time with our comprehensive Logistics ERP.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/request-demo" className="inline-flex items-center gap-2 rounded-lg px-7 py-3.5 font-semibold text-white shadow-lg shadow-primary/30 bg-primary hover:brightness-110 transition">
                  Request a Demo <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            {/* Replaced Mockups with Provided Image */}
            <div className="relative se-fade-up w-full" style={{ animationDelay: '.15s' }}>
              <div className="absolute inset-x-8 top-4 bottom-16 bg-gradient-to-tr from-[#0284c7]/15 to-[#38bdf8]/15 blur-3xl rounded-[48px]" />
              <img 
                src={Logistics} 
                alt="Logistics ERP Dashboard Illustration" 
                className="relative z-10 w-full h-auto object-contain mix-blend-multiply hover:scale-[1.02] transition-transform duration-500"
              />
            </div>

          </div>
        </div>
      </section>

      {/* ================= CORE MODULES ================= */}
      <section className="bg-gradient-to-b from-[#f5f9fd] to-white py-14">
        <div className={CONTAINER}>
          <div className="text-center mb-10">
            <span className="block text-xs font-bold tracking-[0.2em] uppercase text-primary mb-3">Core Modules</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ color: '#13294b' }}>Everything you need in one dashboard</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((feature, index) => (
              <div key={index} className="group h-full flex flex-col bg-white rounded-2xl ring-1 ring-slate-100 p-6 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110" style={{ backgroundColor: `${THEME_COLOR}1A` }}>
                  <feature.icon className="w-6 h-6" style={{ color: THEME_COLOR }} />
                </div>
                <h3 className="font-bold text-slate-800 text-lg mb-3">{feature.title}</h3>
                <p className="text-[13px] text-slate-500 leading-relaxed flex-1">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= INDUSTRY BENEFITS ================= */}
      <section className="py-14 bg-white">
        <div className={CONTAINER}>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="se-fade-up">
              <span className="block text-xs font-bold tracking-[0.2em] uppercase text-[#0284c7] mb-3">
                Why SmartEdz?
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-6" style={{ color: '#13294b' }}>
                Built to reduce transit times and lower fuel costs
              </h2>
              <p className="text-slate-500 mb-8 leading-relaxed">
                Inefficient routing and poor fleet visibility destroy profit margins. Our platform connects your drivers, dispatchers, and warehouses into one synchronized system, ensuring every delivery is cost-effective and on time.
              </p>
              <div className="space-y-6">
                {[
                  { title: 'Dynamic Route Optimization', desc: 'AI-driven algorithms calculate the fastest routes, taking traffic, vehicle capacity, and delivery windows into account.', icon: Route },
                  { title: 'Electronic Proof of Delivery', desc: 'Drivers can collect digital signatures and photos via the mobile app, instantly updating the central dashboard.', icon: ClipboardList },
                  { title: 'Predictive Vehicle Maintenance', desc: 'Prevent costly breakdowns by scheduling automated maintenance alerts based on actual mileage and engine hours.', icon: ShieldCheck }
                ].map((item, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 bg-[#0284c7]/10 text-[#0284c7]">
                      <item.icon className="w-6 h-6" />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-800 text-lg">{item.title}</h4>
                      <p className="text-sm text-slate-500 mt-1">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative rounded-2xl bg-slate-50 border border-slate-100 p-8 shadow-inner se-fade-up" style={{ animationDelay: '0.2s' }}>
              <div className="absolute top-0 right-0 w-64 h-64 bg-[#0284c7]/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
              <h3 className="font-bold text-slate-800 mb-6 flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#0284c7]" /> Fleet Health
              </h3>
              <div className="space-y-4 relative z-10">
                {['Routes Optimized', 'Deliveries On-Time', 'Fleet Fully Active'].map((label, idx) => (
                  <div key={idx} className="bg-white p-4 rounded-xl shadow-sm border border-slate-100 flex items-center justify-between">
                    <span className="font-semibold text-slate-700 text-sm">{label}</span>
                    <span className="flex items-center gap-1 text-xs font-bold text-[#10b981] bg-[#10b981]/10 px-2 py-1 rounded-md">
                      <CheckCircle2 className="w-3 h-3" /> Verified
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= BOTTOM CTA ================= */}
      <section className="py-16">
        <div className={CONTAINER}>
          <div className="relative rounded-[2rem] overflow-hidden bg-gradient-to-br from-[#13294b] to-[#1e447a] p-10 sm:p-16 text-center">
            <div className="absolute top-0 right-0 w-96 h-96 bg-[#0284c7]/20 rounded-full blur-3xl translate-x-1/3 -translate-y-1/3" />
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#0284c7]/10 rounded-full blur-3xl -translate-x-1/3 translate-y-1/3" />
            
            <div className="relative z-10 max-w-2xl mx-auto">
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-4">
                Ready to optimize your logistics?
              </h2>
              <p className="text-slate-300 text-lg mb-8">
                Join forward-thinking transport companies who use SmartEdz to manage their fleets, reduce costs, and deliver faster.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4">
                <Link to="/request-demo" className="inline-flex items-center gap-2 rounded-lg px-8 py-4 font-bold text-[#13294b] bg-white hover:bg-slate-50 transition shadow-lg hover:-translate-y-1">
                  Schedule a Free Demo <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/contact-us" className="inline-flex items-center gap-2 rounded-lg px-8 py-4 font-bold text-white bg-white/10 hover:bg-white/20 transition ring-1 ring-white/20">
                  Talk to Sales
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LogisticsERP;